import { v4 as uuidv4 } from "uuid";
import { calculateModifier } from './rules';
import { HUMAN_NAMES_MALE, HUMAN_NAMES_FEMALE, COMMON_LAST_NAMES, NOBLE_LAST_NAMES } from './nameData';

/**
 * A simple Linear Congruential Generator (LCG) for deterministic randomness.
 * Should be sufficient for game logic.
 */
class SeededRNG {
    constructor(seed) {
        // If no seed provided, use a random one
        this._seed = seed || Math.floor(Math.random() * 2147483647);
        this._state = this._seed;
    }

    // Returns a float between 0 and 1
    random() {
        this._state = (this._state * 9301 + 49297) % 233280;
        return this._state / 233280;
    }

    // Returns an integer between min (inclusive) and max (inclusive)
    range(min, max) {
        return Math.floor(this.random() * (max - min + 1)) + min;
    }

    // Pick a random element from an array
    pick(array) {
        if (!array || array.length === 0) return null;
        return array[this.range(0, array.length - 1)];
    }
}

// --- Data Lists ---
// Removed local constants (now imported from nameData.js)

// Job label for an authored (milestone) NPC: the story's display title when it has one
// ("Militia captain"), else the honorific from the name, else the stat role.
const authoredJobTitle = (spec, npc) => {
    if (spec.title) return spec.title.charAt(0).toUpperCase() + spec.title.slice(1);
    return npc.title || spec.role;
};

const ROLES = {
    "Villager": {
        possibleTitles: ["Citizen", "Peasant", "Farmer", "Laborer", "Elder"],
        defaultClass: "Commoner",
        baseStats: { Strength: 10, Dexterity: 10, Constitution: 10, Intelligence: 10, Wisdom: 10, Charisma: 10 },
        inventory: ["Simple Clothes", "Bread"],
        hpRange: [4, 8]
    },
    "Guard": {
        possibleTitles: ["Sentry", "Watchman", "Constable", "Captain", "Sergeant", "Lieutenant"],
        defaultClass: "Fighter",
        baseStats: { Strength: 14, Dexterity: 12, Constitution: 13, Intelligence: 10, Wisdom: 11, Charisma: 10 },
        inventory: ["Chain Shirt", ["Spear", "Longsword", "Halberd"], "Shield", "Whistle"],
        hpRange: [12, 20]
    },
    "Merchant": {
        possibleTitles: ["Shopkeeper", "Trader", "Vendor", "Master", "Supplier"],
        defaultClass: "Expert",
        baseStats: { Strength: 10, Dexterity: 11, Constitution: 10, Intelligence: 13, Wisdom: 12, Charisma: 14 },
        inventory: ["Fine Clothes", "Ledger", "Ink & Quill"],
        hpRange: [6, 10]
    },
    "Noble": {
        possibleTitles: {
            Male: ["Lord", "Baron", "Duke", "Earl", "Count", "Viscount", "Sir"],
            Female: ["Lady", "Baroness", "Duchess", "Countess", "Countess", "Viscountess", "Dame"]
        },
        defaultClass: "Aristocrat",
        baseStats: { Strength: 9, Dexterity: 12, Constitution: 9, Intelligence: 12, Wisdom: 11, Charisma: 15 },
        inventory: ["Silk Clothes", "Signet Ring", "Jewelry"],
        hpRange: [6, 12]
    },
    "Noble Child": {
        possibleTitles: {
            Male: ["Young Lord", "Master"],
            Female: ["Young Lady", "Miss"]
        },
        defaultClass: "Aristocrat",
        baseStats: { Strength: 6, Dexterity: 10, Constitution: 8, Intelligence: 10, Wisdom: 8, Charisma: 12 },
        inventory: ["Fine Clothes", "Toy Sword", "Doll"],
        hpRange: [4, 6]
    },
    // Lesser nobility / landed gentry
    "Gentry": {
        possibleTitles: {
            Male: ["Sir", "Master", "Squire", "Gentleman", "Steward"],
            Female: ["Dame", "Mistress", "Lady", "Gentlewoman", "Stewardess"]
        },
        defaultClass: "Aristocrat",
        baseStats: { Strength: 10, Dexterity: 11, Constitution: 10, Intelligence: 12, Wisdom: 12, Charisma: 13 },
        inventory: ["Fine Clothes", "Riding Boots", ["Signet Ring", "Silver Brooch", "Gold Chain"]],
        hpRange: [6, 12]
    },
    "Gentry Child": {
        possibleTitles: {
            Male: ["Young Master", "Master"],
            Female: ["Young Mistress", "Miss"]
        },
        defaultClass: "Aristocrat",
        baseStats: { Strength: 7, Dexterity: 10, Constitution: 8, Intelligence: 11, Wisdom: 9, Charisma: 12 },
        inventory: ["Fine Clothes", ["Book of Heraldry", "Wooden Practice Sword", "Embroidery Kit"]],
        hpRange: [4, 6]
    },
    "Criminal": {
        possibleTitles: ["Thief", "Bandit", "Cutpurse", "Thug", "Smuggler"],
        defaultClass: "Rogue",
        baseStats: { Strength: 12, Dexterity: 15, Constitution: 12, Intelligence: 10, Wisdom: 10, Charisma: 11 },
        inventory: [["Leather Armor", "Padded Armor"], ["Dagger", "Shortsword"], "Thieves' Tools", "Stolen Goods"],
        hpRange: [10, 16]
    },
    "Tavern Keeper": {
        possibleTitles: {
            Male: ["Innkeeper", "Barkeep", "Owner", "Host"],
            Female: ["Innkeeper", "Barkeep", "Owner", "Hostess"]
        },
        defaultClass: "Expert",
        baseStats: { Strength: 10, Dexterity: 10, Constitution: 10, Intelligence: 12, Wisdom: 13, Charisma: 14 },
        inventory: ["Apron", "Keys to the Cellar", "Tankard", "Towel"],
        hpRange: [6, 12]
    },
    "Tavern Worker": {
        possibleTitles: {
            Male: ["Server", "Cook", "Stablehand", "Potboy"],
            Female: ["Server", "Cook", "Maid", "Hostess"]
        },
        defaultClass: "Commoner",
        baseStats: { Strength: 11, Dexterity: 12, Constitution: 11, Intelligence: 9, Wisdom: 10, Charisma: 10 },
        inventory: ["Simple Clothes", "Dirty Apron", ["Broom", "Tray", "Bucket"]],
        hpRange: [4, 8]
    },
    // Guild Functions
    "Guild Master": {
        possibleTitles: ["Grandmaster", "High Artisan", "Guildmaster", "Director", "Foreman"],
        defaultClass: "Expert",
        baseStats: { Strength: 12, Dexterity: 12, Constitution: 12, Intelligence: 14, Wisdom: 14, Charisma: 14 },
        inventory: ["Guild Badge", "Masterwork Tool", "Fine Clothes", "Ledger"],
        hpRange: [10, 20]
    },
    "Guild Member": {
        possibleTitles: ["Journeyman", "Apprentice", "Member", "Initiate", "Adept"],
        defaultClass: "Expert",
        baseStats: { Strength: 11, Dexterity: 12, Constitution: 11, Intelligence: 12, Wisdom: 10, Charisma: 10 },
        inventory: ["Guild Badge", "Tools", "Apron"],
        hpRange: [6, 12]
    },
    // Religious Functions
    "Priest": {
        possibleTitles: {
            Male: ["Father", "High Priest", "Curate", "Bishop", "Elder"],
            Female: ["Mother", "High Priestess", "Bishop", "Elder"]
        },
        defaultClass: "Cleric",
        baseStats: { Strength: 10, Dexterity: 10, Constitution: 12, Intelligence: 12, Wisdom: 16, Charisma: 14 },
        inventory: ["Holy Symbol", "Vestments", "Prayer Book", "Incense"],
        hpRange: [12, 24]
    },
    "Acolyte": {
        possibleTitles: {
            Male: ["Brother", "Novice", "Initiate", "Deacon"],
            Female: ["Sister", "Novice", "Initiate", "Deacon"]
        },
        defaultClass: "Adept",
        baseStats: { Strength: 10, Dexterity: 10, Constitution: 10, Intelligence: 11, Wisdom: 13, Charisma: 12 },
        inventory: ["Holy Symbol", "Simple Robes", "Candle"],
        hpRange: [6, 12]
    },
    // Specialized Trades
    "Blacksmith": {
        possibleTitles: {
            Male: ["Smith", "Blacksmith", "Armorer", "Ironwright", "Master Smith"],
            Female: ["Smith", "Blacksmith", "Armorer", "Ironwright", "Master Smith"]
        },
        defaultClass: "Expert",
        baseStats: { Strength: 15, Dexterity: 10, Constitution: 14, Intelligence: 10, Wisdom: 11, Charisma: 10 },
        inventory: ["Leather Apron", "Hammer", "Tongs", "Iron Scraps"],
        hpRange: [10, 18]
    },
    "Farmer": {
        possibleTitles: ["Farmer", "Crofter", "Husbandman", "Harvester", "Plowman"],
        defaultClass: "Commoner",
        baseStats: { Strength: 13, Dexterity: 11, Constitution: 12, Intelligence: 10, Wisdom: 11, Charisma: 10 },
        inventory: ["Rough Clothes", ["Pitchfork", "Scythe", "Sickle"], "Straw Hat"],
        hpRange: [6, 10]
    },
    // Knowledge & Scholarly
    "Archivist": {
        possibleTitles: {
            Male: ["Lorekeeper", "Archivist", "Curator", "Head Scholar", "Sage"],
            Female: ["Lorekeeper", "Archivist", "Curator", "Head Scholar", "Sage"]
        },
        defaultClass: "Expert",
        baseStats: { Strength: 8, Dexterity: 10, Constitution: 10, Intelligence: 16, Wisdom: 14, Charisma: 11 },
        inventory: ["Reading Glasses", "Quill & Ink", "Leather-Bound Journal", "Ring of Keys"],
        hpRange: [6, 10]
    },
    "Librarian": {
        possibleTitles: {
            Male: ["Scribe", "Bookkeeper", "Assistant", "Cataloger", "Page"],
            Female: ["Scribe", "Bookkeeper", "Assistant", "Cataloger", "Page"]
        },
        defaultClass: "Expert",
        baseStats: { Strength: 8, Dexterity: 11, Constitution: 9, Intelligence: 14, Wisdom: 12, Charisma: 10 },
        inventory: ["Simple Robes", "Stack of Scrolls", "Candle", "Bookmark"],
        hpRange: [4, 8]
    },
    // Alchemy & Potions
    "Alchemist": {
        possibleTitles: {
            Male: ["Apothecary", "Alchemist", "Herbalist", "Potioneer", "Brewer"],
            Female: ["Apothecary", "Alchemist", "Herbalist", "Potioneer", "Brewer"]
        },
        defaultClass: "Expert",
        baseStats: { Strength: 9, Dexterity: 13, Constitution: 11, Intelligence: 15, Wisdom: 13, Charisma: 10 },
        inventory: ["Stained Apron", "Mortar & Pestle", "Herb Pouch", ["Healing Potion", "Antidote", "Flask of Acid"]],
        hpRange: [6, 12]
    },
    // Industrial
    "Foundry Master": {
        possibleTitles: {
            Male: ["Smelter", "Foreman", "Furnace Master", "Ironworker", "Caster"],
            Female: ["Smelter", "Foreman", "Furnace Master", "Ironworker", "Caster"]
        },
        defaultClass: "Expert",
        baseStats: { Strength: 14, Dexterity: 10, Constitution: 15, Intelligence: 11, Wisdom: 10, Charisma: 9 },
        inventory: ["Heavy Apron", "Long Tongs", "Fire-Resistant Gloves", "Iron Ingot"],
        hpRange: [12, 20]
    },
    "Foundry Worker": {
        possibleTitles: ["Stoker", "Slagman", "Ore Handler", "Bellows Operator", "Apprentice"],
        defaultClass: "Commoner",
        baseStats: { Strength: 13, Dexterity: 10, Constitution: 14, Intelligence: 9, Wisdom: 10, Charisma: 9 },
        inventory: ["Soot-Stained Clothes", "Shovel", "Waterskin"],
        hpRange: [8, 14]
    },
    // Banking & Finance
    "Banker": {
        possibleTitles: {
            Male: ["Banker", "Financier", "Moneylender", "Treasurer", "Vault Keeper"],
            Female: ["Banker", "Financier", "Moneylender", "Treasurer", "Vault Keeper"]
        },
        defaultClass: "Expert",
        baseStats: { Strength: 9, Dexterity: 11, Constitution: 10, Intelligence: 15, Wisdom: 14, Charisma: 13 },
        inventory: ["Fine Clothes", "Ledger", "Ink & Quill", "Ring of Keys", "Coin Purse"],
        hpRange: [6, 12]
    },
    "Bank Clerk": {
        possibleTitles: ["Teller", "Clerk", "Bookkeeper", "Accountant", "Apprentice"],
        defaultClass: "Expert",
        baseStats: { Strength: 9, Dexterity: 11, Constitution: 10, Intelligence: 13, Wisdom: 12, Charisma: 11 },
        inventory: ["Simple Clothes", "Ledger", "Ink & Quill", "Abacus"],
        hpRange: [4, 8]
    },
    // Logistics & Trade
    "Warehouse Master": {
        possibleTitles: {
            Male: ["Quartermaster", "Stockmaster", "Depot Chief", "Supply Master", "Warden"],
            Female: ["Quartermaster", "Stockmaster", "Depot Chief", "Supply Master", "Warden"]
        },
        defaultClass: "Expert",
        baseStats: { Strength: 12, Dexterity: 11, Constitution: 11, Intelligence: 13, Wisdom: 12, Charisma: 12 },
        inventory: ["Work Clothes", "Manifest Ledger", "Ring of Keys", "Measuring Rod"],
        hpRange: [8, 14]
    },
    // --- occupants for the newer building types ---
    "Miller": {
        possibleTitles: ["Miller", "Master Miller", "Grain Miller"],
        defaultClass: "Commoner",
        baseStats: { Strength: 13, Dexterity: 10, Constitution: 12, Intelligence: 10, Wisdom: 11, Charisma: 10 },
        inventory: ["Flour-dusted Apron", "Sack of Grain"],
        hpRange: [6, 10]
    },
    "Tailor": {
        possibleTitles: {
            Male: ["Tailor", "Clothier", "Weaver", "Garmentier"],
            Female: ["Tailor", "Seamstress", "Clothier", "Weaver"]
        },
        defaultClass: "Expert",
        baseStats: { Strength: 9, Dexterity: 13, Constitution: 10, Intelligence: 11, Wisdom: 11, Charisma: 12 },
        inventory: ["Fine Clothes", "Needle & Thread", "Bolt of Cloth"],
        hpRange: [5, 9]
    },
    "Fletcher": {
        possibleTitles: ["Fletcher", "Bowyer", "Arrowsmith", "Master Fletcher"],
        defaultClass: "Expert",
        baseStats: { Strength: 11, Dexterity: 13, Constitution: 10, Intelligence: 11, Wisdom: 12, Charisma: 10 },
        inventory: ["Leather Apron", "Bundle of Arrows", ["Shortbow", "Longbow"]],
        hpRange: [6, 10]
    },
    "Stablemaster": {
        possibleTitles: ["Stablemaster", "Hostler", "Groom", "Horsemaster"],
        defaultClass: "Commoner",
        baseStats: { Strength: 12, Dexterity: 12, Constitution: 12, Intelligence: 10, Wisdom: 12, Charisma: 10 },
        inventory: ["Riding Boots", "Horse Brush", "Coil of Rope"],
        hpRange: [7, 11]
    },
    "Harbormaster": {
        possibleTitles: ["Harbormaster", "Dockmaster", "Port Warden", "Quaymaster"],
        defaultClass: "Expert",
        baseStats: { Strength: 11, Dexterity: 11, Constitution: 12, Intelligence: 13, Wisdom: 13, Charisma: 12 },
        inventory: ["Oilskin Coat", "Brass Spyglass", "Shipping Ledger"],
        hpRange: [8, 12]
    },
    "Boatwright": {
        possibleTitles: ["Boatwright", "Shipwright", "Ferryman", "Oarmaker"],
        defaultClass: "Expert",
        baseStats: { Strength: 12, Dexterity: 12, Constitution: 12, Intelligence: 11, Wisdom: 12, Charisma: 10 },
        inventory: ["Caulking Mallet", "Tar Pot", "Coil of Rope"],
        hpRange: [7, 11]
    },
    "Mage": {
        possibleTitles: {
            Male: ["Mage", "Wizard", "Sorcerer", "Enchanter", "Archmage"],
            Female: ["Mage", "Witch", "Sorceress", "Enchantress", "Archmage"]
        },
        defaultClass: "Wizard",
        baseStats: { Strength: 8, Dexterity: 11, Constitution: 10, Intelligence: 16, Wisdom: 13, Charisma: 12 },
        inventory: ["Robes", "Spellbook", "Arcane Focus"],
        hpRange: [5, 9]
    },
    "Jailer": {
        possibleTitles: ["Jailer", "Gaoler", "Warden", "Turnkey"],
        defaultClass: "Fighter",
        baseStats: { Strength: 14, Dexterity: 10, Constitution: 13, Intelligence: 9, Wisdom: 11, Charisma: 8 },
        inventory: ["Leather Armor", "Cudgel", "Ring of Keys"],
        hpRange: [10, 16]
    },
    "Magistrate": {
        possibleTitles: {
            Male: ["Mayor", "Magistrate", "Burgomaster", "Reeve", "Alderman"],
            Female: ["Mayor", "Magistrate", "Burgomaster", "Reeve", "Alderwoman"]
        },
        defaultClass: "Aristocrat",
        baseStats: { Strength: 9, Dexterity: 10, Constitution: 10, Intelligence: 14, Wisdom: 14, Charisma: 15 },
        inventory: ["Fine Clothes", "Chain of Office", "Town Charter"],
        hpRange: [6, 11]
    }
};

const ALIGNMENTS = [
    "Lawful Good", "Neutral Good", "Chaotic Good",
    "Lawful Neutral", "True Neutral", "Chaotic Neutral",
    "Lawful Evil", "Neutral Evil", "Chaotic Evil"
];

const TRINKETS = [
    "Brass Key", "Carved Wooden Duck", "Silver Locket", "Strange Coin", "Dice Set",
    "Dried Rabbit's Foot", "Letter from home", "Map fragment", "Shiny rock", "Bone whistle",
    "Copper Ring", "Old pipe", "Deck of cards", "Small mirror", "Bag of marbles"
];

// --- Generator Functions ---

/**
 * Picks a random name from the dataset.
 * @param {string} gender "Male", "Female", or null/other
 * @param {Object} [rng] Optional SeededRNG instance
 * @param {string} [lastName] Optional fixed last name
 * @returns {string}
 */
export const generateName = (gender, rng = null, lastName = null) => {
    const pick = (arr) => {
        if (rng) return rng.pick(arr);
        return arr[Math.floor(Math.random() * arr.length)];
    };

    let firstName;
    if (gender === "Male") {
        firstName = pick(HUMAN_NAMES_MALE);
    } else if (gender === "Female") {
        firstName = pick(HUMAN_NAMES_FEMALE);
    } else {
        firstName = pick([...HUMAN_NAMES_MALE, ...HUMAN_NAMES_FEMALE]);
    }

    // Commoners draw from the common pool; noble surnames belong to the houses that bear them.
    const selectedLastName = lastName || pick(COMMON_LAST_NAMES);
    return `${firstName} ${selectedLastName}`;
};

/**
 * Generates a full NPC object deterministically based on input options and/or a seed.
 * 
 * @param {Object} options Configuration options
 * @param {string} [options.seed] Seed for deterministic generation. If null, random seed used.
 * @param {string} [options.race="Human"] Race of the NPC.
 * @param {string} [options.gender] "Male", "Female", or null (random).
 * @param {string} [options.role] Specific role key (e.g. "Guard"). If null, random role.
 * @param {number} [options.level=1] Level of the NPC.
 */
export const generateNPC = (options = {}) => {
    // 1. Initialize RNG
    // Use provided seed or generate a random integer seed
    const seed = options.seed || Math.floor(Math.random() * 1000000);
    const rng = new SeededRNG(seed);

    // 2. Determine basic properties
    const race = options.race || "Human"; // Default to Human for now
    const gender = options.gender || (rng.random() > 0.5 ? "Male" : "Female");

    // 3. Determine Role
    const roleKeys = Object.keys(ROLES);
    const roleKey = options.role && ROLES[options.role] ? options.role : rng.pick(roleKeys);
    const roleData = ROLES[roleKey];

    // 4. Determine Title
    let title;
    let selectedTitleIndex = -1;
    if (options.title) {
        title = options.title;
    } else {
        const titles = roleData.possibleTitles;
        if (Array.isArray(titles)) {
            selectedTitleIndex = options.titleIndex !== undefined ? options.titleIndex : rng.range(0, titles.length - 1);
            title = titles[selectedTitleIndex];
        } else if (typeof titles === 'object') {
            const list = titles[gender] || titles.Male || [];
            selectedTitleIndex = options.titleIndex !== undefined ? options.titleIndex : rng.range(0, list.length - 1);
            title = list[selectedTitleIndex] || "Citizen";
        }
    }

    // 5. Determine Age
    let age;
    if (roleKey === "Noble Child" || roleKey === "Gentry Child" || title === "Child") {
        age = rng.range(4, 15);
    } else if (title.includes("Elder")) {
        age = rng.range(60, 90);
    } else {
        // Weighted random for adults: mostly 20-50, some older
        const roll = rng.random();
        if (roll < 0.6) {
            age = rng.range(18, 35); // Young adult
        } else if (roll < 0.9) {
            age = rng.range(36, 55); // Middle aged
        } else {
            age = rng.range(56, 75); // Older
        }
    }

    // 6. Generate Name
    const fullName = generateName(gender, rng, options.lastName);

    // 6. Generate Stats (Base + Variance)
    const stats = { ...roleData.baseStats };
    // Add some simple variance (-1 to +2) to each stat to make them unique
    Object.keys(stats).forEach(stat => {
        const variance = rng.range(-1, 2);
        stats[stat] = Math.max(1, stats[stat] + variance); // Ensure stat doesn't drop below 1
    });

    // 7. Calculate Derived Stats
    const level = options.level || 1;
    const conMod = calculateModifier(stats.Constitution);
    const baseHp = rng.range(roleData.hpRange[0], roleData.hpRange[1]);
    const maxHP = Math.max(1, baseHp + (conMod * level)); // HP formula approximation

    // 8. Other properties
    let availableAlignments = ALIGNMENTS;
    if (options.noEvil) {
        availableAlignments = ALIGNMENTS.filter(a => !a.includes("Evil"));
    }
    const alignment = rng.pick(availableAlignments);
    const npcClass = roleData.defaultClass;

    // 9. Inventory
    // Start with role-based items
    let inventory = [...roleData.inventory].map(item => {
        // Resolve choices: if item is an array, pick one
        if (Array.isArray(item)) {
            return rng.pick(item);
        }
        return item;
    });

    // Add Wealth based on Role/Class (Simple approximation)
    let coins = 0;
    if (roleKey === "Noble Child" || roleKey === "Gentry Child") {
        coins = rng.range(2, 10) + " Silver Pieces (Allowance)";
    } else if (roleKey === "Noble" || roleKey === "Merchant") {
        coins = rng.range(20, 100) + " Gold Pieces";
    } else if (roleKey === "Gentry") {
        coins = rng.range(10, 50) + " Gold Pieces";
    } else if (roleKey === "Guard" || roleKey === "Tavern Keeper") {
        coins = rng.range(5, 20) + " Silver Pieces";
    } else {
        coins = rng.range(2, 15) + " Copper Pieces";
    }
    inventory.push(coins);

    // Add Trinket (30% chance)
    if (rng.random() < 0.3) {
        inventory.push(rng.pick(TRINKETS));
    }

    return {
        id: uuidv4(),
        seed: seed, // Store seed for reproduction
        name: fullName,
        age: age,
        gender: gender,
        race: race,
        role: roleKey,
        title: title,
        class: npcClass,
        level: level,
        alignment: alignment,
        stats: stats,
        hp: {
            current: maxHP,
            max: maxHP
        },
        inventory: inventory,
        selectedTitleIndex: selectedTitleIndex, // Return the index for spouse matching
        isNPC: true
    };
};

// --- Town Population Logic ---

/**
 * scanning the town map data and generating NPCs for employment buildings.
 * @param {Object} townMapData Output from generateTownMap
 * @param {string|number} seed Seed for deterministic generation
 * @param {Array} milestoneNpcs Optional canonical/authored NPCs to place in this
 *   town (from getMilestoneNpcsForTown). Each entry: { id, name, role, personality,
 *   building: { type, name }|null, ... }. When one targets a service building, the
 *   canonical NPC REPLACES the procedural staff for that building.
 * @returns {Array} List of generated NPCs with location data
 */
export const populateTown = (townMapData, seed, milestoneNpcs = []) => {
    const rng = new SeededRNG(seed);
    const npcs = [];
    const { mapData, width, height, townSize, townName } = townMapData;

    // 1. ANALYZE MAP FOR BUILDINGS & SITES
    const residentialSites = [];
    const serviceBuildings = [];
    const workSites = []; // Fields, Barns
    let vocationSlots = null;

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const tile = mapData[y][x];
            if (tile.type === 'building') {
                if (tile.buildingType === 'house' || tile.buildingType === 'manor' || tile.buildingType === 'keep') {
                    residentialSites.push({ x, y, type: tile.buildingType, name: tile.buildingName });
                } else {
                    // Civic buildings (harbormaster, mill, jail, magetower, shrine...) are never
                    // named by the town generator, and cached town maps can't be renamed
                    // retroactively, so fall back to the town name: "Harbormaster of Willowdale",
                    // never "Harbormaster of undefined".
                    serviceBuildings.push({ x, y, type: tile.buildingType, name: tile.buildingName || townName });
                }
            } else if (tile.type === 'farm_field' || (tile.type === 'building' && tile.buildingType === 'barn')) {
                workSites.push({ x, y, type: tile.type === 'farm_field' ? 'field' : 'barn' });
            }
        }
    }

    // Helper to add NPC with linkage
    // Gender is DEALT from a balanced, seeded deck rather than flipped per NPC
    // (maintainer directive, playtest 2026-07-04): fair independent coin flips
    // produce same-gender streaks (runs of 5-6 building heads are expected over
    // ~85 buildings) that read as bias in play. Dealing in shuffled Male/Female
    // pairs pins every town at an exact 50/50 and caps dealt runs at 2. NPCs
    // with an explicit gender (spouses, authored NPCs) bypass the deck.
    let genderDeck = [];
    const dealGender = () => {
        if (genderDeck.length === 0) {
            genderDeck = rng.random() < 0.5 ? ['Male', 'Female'] : ['Female', 'Male'];
        }
        return genderDeck.pop();
    };

    const addNPC = (role, workplace, home, options = {}) => {
        const npcSeed = parseInt(seed) + (workplace.x * 1000) + (workplace.y * 100) + rng.range(0, 9999);
        const opts = { seed: npcSeed, role, noEvil: true, ...options };
        if (!opts.gender) opts.gender = dealGender();
        const npc = generateNPC(opts);

        npc.location = {
            x: workplace.x,
            y: workplace.y,
            buildingName: workplace.name || workplace.type || "Outdoors",
            buildingType: workplace.type,
            homeCoords: home ? { x: home.x, y: home.y } : null
        };
        npcs.push(npc);
        return npc;
    };

    // --- Canonical (milestone-authored) NPC placement ---
    // Match an authored NPC to a service building, then place it INSTEAD of the
    // procedural staff. Matching is by building NAME first (injectQuestBuildings
    // renames the quest building to the authored name, so the name is the reliable
    // key), then falls back to building type. Each authored NPC is placed at most once.
    const authoredNpcs = Array.isArray(milestoneNpcs) ? milestoneNpcs : [];
    const placedAuthoredIds = new Set();

    const matchAuthoredNpc = (b) => {
        if (authoredNpcs.length === 0) return null;
        const byName = authoredNpcs.find((n) =>
            !placedAuthoredIds.has(n.id) &&
            n.building?.name && b.name &&
            n.building.name.toLowerCase() === b.name.toLowerCase()
        );
        if (byName) return byName;
        return authoredNpcs.find((n) =>
            !placedAuthoredIds.has(n.id) &&
            n.building?.type && n.building.type === b.type
        ) || null;
    };

    // Place a canonical NPC in building `b`, forcing its authored identity. The
    // NPC's personality is preserved on the object so it can reach the AI prompt.
    const addAuthoredNPC = (b, spec) => {
        const role = ROLES[spec.role] ? spec.role : 'Guard';
        const npc = addNPC(role, b, b);
        npc.name = spec.name;
        if (spec.gender) npc.gender = spec.gender; // keep the icon/pronoun consistent with the authored name
        // The authored name usually leads with an honorific ("Captain Marta",
        // "Keeper Najwa"); use it as the title so display/labels stay consistent.
        if (spec.name.includes(' ')) npc.title = spec.name.split(' ')[0];
        if (spec.personality) npc.personality = spec.personality;
        npc.milestoneNpcId = spec.id;
        npc.milestoneId = spec.milestoneId;
        npc.job = `${authoredJobTitle(spec, npc)} at ${b.name || b.type}`;
        placedAuthoredIds.add(spec.id);
        return npc;
    };

    // 2. TOWN LEADER LOGIC
    let leaderSet = false;
    // The lord lives in the keep when there is one; only keep-less towns seat the leader in
    // a manor. (Scanning for "keep or manor" took whichever came first on the map, so a
    // manor could win and the keep then fell through to the commoner branch below.)
    let mainResidence = residentialSites.find(r => r.type === 'keep') || residentialSites.find(r => r.type === 'manor');

    if (mainResidence) {
        // High Noble Leader and Family
        let familyName;
        if (mainResidence.name && mainResidence.name !== "Manor" && mainResidence.name !== "Keep") {
            familyName = mainResidence.name.split(' ')[0];
        } else if (rng.random() < 0.4) {
            // 40% chance the noble house takes the name of the town (e.g. House Everfell)
            // Strip common suffixes if present
            familyName = townName.replace(/(ton|burg|shire|hold|wick|stead)$/, '');
        } else {
            familyName = rng.pick(NOBLE_LAST_NAMES);
        }

        // 1. Head of House
        const head = addNPC("Noble", mainResidence, mainResidence, { lastName: familyName });
        if (head.age < 25) head.age = rng.range(25, 45);
        head.job = `${head.title} of ${townName}`;

        // 2. Spouse
        const spouseGender = head.gender === "Male" ? "Female" : "Male";
        const spouse = addNPC("Noble", mainResidence, mainResidence, {
            lastName: familyName,
            gender: spouseGender,
            titleIndex: head.selectedTitleIndex // Match title index for spouse correlation
        });
        spouse.age = head.age + rng.range(-4, 2);
        if (spouse.age < 20) spouse.age = 20;
        spouse.job = `${spouse.title} of ${townName}`;

        // 3. Children (1-3) with coherent ages
        const nobleYoungestParent = Math.min(head.age, spouse.age);
        const nobleOldestChildAge = Math.min(15, Math.max(1, nobleYoungestParent - 16));
        const childCount = rng.range(1, 3);
        let nobleChildAge = nobleOldestChildAge;
        for (let i = 0; i < childCount; i++) {
            const child = addNPC("Noble Child", mainResidence, mainResidence, { lastName: familyName });
            child.age = nobleChildAge;
            nobleChildAge = Math.max(1, nobleChildAge - rng.range(1, 3));
            child.job = `${child.title} of the House ${familyName}`;
        }

        leaderSet = true;
    } else if (residentialSites.length > 0) {
        // Village Elder / Headman
        const elderHome = residentialSites[0]; // Take first house
        const elder = addNPC("Villager", elderHome, elderHome, { title: townSize === 'hamlet' ? 'Headman' : 'Elder' });
        elder.job = `Leader of ${townName}`;
        leaderSet = true;
    }

    // 3. SERVICE BUILDINGS (Inns, Shops, Temples, Blacksmiths)
    serviceBuildings.forEach(b => {
        // Milestone-authored NPC (e.g. Captain Marta at the Briarwood Militia Hall)
        // REPLACES the procedural staffing for this building.
        const authored = matchAuthoredNpc(b);
        if (authored) {
            addAuthoredNPC(b, authored);
            return;
        }
        if (b.type === 'tavern' || b.type === 'inn') {
            const keeper = addNPC("Tavern Keeper", b, b, { title: "Owner" });
            keeper.job = `Owner of ${b.name}`;
            const spouseGender = keeper.gender === "Male" ? "Female" : "Male";
            const spouse = addNPC("Tavern Keeper", b, b, {
                gender: spouseGender,
                lastName: keeper.lastName,
                titleIndex: keeper.selectedTitleIndex // Sync titles like 'Host' & 'Hostess'
            });
            spouse.job = `Co-Owner of ${b.name}`;
        } else if (b.type === 'shop' || b.type === 'market') {
            // Check for "Name's Goods" pattern in building name
            let forcedOptions = {};
            if (b.name && b.name.includes("'s ")) {
                const possibleName = b.name.split("'s ")[0];
                if (HUMAN_NAMES_MALE.includes(possibleName)) {
                    forcedOptions = { firstName: possibleName, gender: "Male" };
                } else if (HUMAN_NAMES_FEMALE.includes(possibleName)) {
                    forcedOptions = { firstName: possibleName, gender: "Female" };
                }
            }

            const merchant = addNPC("Merchant", b, b, forcedOptions);
            merchant.job = `Proprietor of ${b.name}`;
            const spouseGender = merchant.gender === "Male" ? "Female" : "Male";
            const spouse = addNPC("Merchant", b, b, {
                gender: spouseGender,
                lastName: merchant.lastName,
                titleIndex: merchant.selectedTitleIndex
            });
            spouse.job = `Merchant at ${b.name}`;
        } else if (b.type === 'temple') {
            const priest = addNPC("Priest", b, b);
            priest.job = `${priest.title} of ${b.name}`;
            const spouseGender = priest.gender === "Male" ? "Female" : "Male";
            const spouse = addNPC("Acolyte", b, b, {
                gender: spouseGender,
                lastName: priest.lastName,
                titleIndex: priest.selectedTitleIndex // Father & Sister (or Mother & Brother)
            });
            spouse.job = `${spouse.title} of ${b.name}`;
        } else if (b.type === 'blacksmith') {
            const smith = addNPC("Blacksmith", b, b);
            smith.job = `Master Smith of ${b.name}`;
            const spouseGender = smith.gender === "Male" ? "Female" : "Male";
            const spouse = addNPC("Blacksmith", b, b, {
                gender: spouseGender,
                lastName: smith.lastName,
                titleIndex: smith.selectedTitleIndex
            });
            spouse.job = `Assistant Smith at ${b.name}`;
        } else if (b.type === 'guild') {
            const master = addNPC("Guild Master", b, b);
            master.job = `Master of ${b.name}`;
        } else if (b.type === 'archives' || b.type === 'library') {
            const archivist = addNPC("Archivist", b, b);
            archivist.job = `Head of ${b.name}`;
            const assistant = addNPC("Librarian", b, b, {
                gender: archivist.gender === "Male" ? "Female" : "Male"
            });
            assistant.job = `${assistant.title} at ${b.name}`;
        } else if (b.type === 'alchemist') {
            const alchemist = addNPC("Alchemist", b, b);
            alchemist.job = `Proprietor of ${b.name}`;
        } else if (b.type === 'foundry') {
            const master = addNPC("Foundry Master", b, b);
            master.job = `Master of ${b.name}`;
            const worker = addNPC("Foundry Worker", b, b);
            worker.job = `Worker at ${b.name}`;
        } else if (b.type === 'warehouse') {
            const master = addNPC("Warehouse Master", b, b);
            master.job = `${master.title} of ${b.name}`;
        } else if (b.type === 'bank') {
            const banker = addNPC("Banker", b, b);
            banker.job = `Director of ${b.name}`;
            const clerk = addNPC("Bank Clerk", b, b);
            clerk.job = `Clerk at ${b.name}`;
        } else if (b.type === 'mill') {
            // family-run mill (proprietor + spouse, titles synced)
            const miller = addNPC("Miller", b, b);
            miller.job = `Miller of ${b.name}`;
            const spouse = addNPC("Miller", b, b, {
                gender: miller.gender === "Male" ? "Female" : "Male",
                lastName: miller.lastName,
                titleIndex: miller.selectedTitleIndex
            });
            spouse.job = `Miller at ${b.name}`;
        } else if (b.type === 'tailor') {
            const tailor = addNPC("Tailor", b, b);
            tailor.job = `Proprietor of ${b.name}`;
            const spouse = addNPC("Tailor", b, b, {
                gender: tailor.gender === "Male" ? "Female" : "Male",
                lastName: tailor.lastName,
                titleIndex: tailor.selectedTitleIndex
            });
            spouse.job = `${spouse.title} at ${b.name}`;
        } else if (b.type === 'fletcher') {
            const fletcher = addNPC("Fletcher", b, b);
            fletcher.job = `Master of ${b.name}`;
        } else if (b.type === 'apothecary') {
            const apothecary = addNPC("Alchemist", b, b, { title: "Apothecary" });
            apothecary.job = `Apothecary of ${b.name}`;
        } else if (b.type === 'stables') {
            const master = addNPC("Stablemaster", b, b);
            master.job = `${master.title} of ${b.name}`;
            const hand = addNPC("Villager", b, b, { title: "Stablehand" });
            hand.job = `Stablehand at ${b.name}`;
        } else if (b.type === 'harbormaster') {
            const hm = addNPC("Harbormaster", b, b);
            hm.job = `${hm.title} of ${b.name}`;
        } else if (b.type === 'boathouse') {
            // Canal-city boathouse (water towns #65): proprietor pattern, sibling of
            // the harbormaster branch above. Unnamed civic building, so b.name is the
            // town-name fallback ("Boatwright of Saltmere").
            const bw = addNPC("Boatwright", b, b);
            bw.job = `${bw.title} of ${b.name}`;
        } else if (b.type === 'magetower') {
            const mage = addNPC("Mage", b, b);
            mage.job = `${mage.title} of ${b.name}`;
        } else if (b.type === 'jail') {
            const jailer = addNPC("Jailer", b, b);
            jailer.job = `${jailer.title} of ${b.name}`;
        } else if (b.type === 'townhall') {
            // The town hall is also the moot court; the constable (the medieval equivalent
            // of a sheriff) keeps the peace from here — no separate sheriff's office.
            const mayor = addNPC("Magistrate", b, b);
            mayor.job = `${mayor.title} of ${townName}`;
            const clerk = addNPC("Merchant", b, b, { title: "Clerk" });
            clerk.job = `Town Clerk at ${b.name}`;
            const constable = addNPC("Guard", b, b, { title: rng.pick(["Constable", "Reeve", "Bailiff"]) });
            constable.job = `${constable.title} of ${townName}`;
        } else if (b.type === 'shrine') {
            const priest = addNPC("Priest", b, b);
            priest.job = `${priest.title} of ${b.name}`;
        } else if (b.type === 'barracks') {
            // Militia hall / garrison: a ranking officer plus a guard or two on duty.
            const captain = addNPC("Guard", b, b, { title: rng.pick(["Captain", "Sergeant", "Lieutenant"]) });
            captain.job = `Commander of ${b.name}`;
            const guardCount = rng.pick([1, 2]);
            for (let i = 0; i < guardCount; i++) {
                const guard = addNPC("Guard", b, b, { title: rng.pick(["Sentry", "Watchman", "Man-at-arms"]) });
                guard.job = `Guard at ${b.name}`;
            }
        } else if (b.type === 'workshop') {
            // Tinker's / artificer's workshop (a crafts-person, reusing the smith archetype).
            const tinker = addNPC("Blacksmith", b, b, { title: "Tinker" });
            tinker.job = `Proprietor of ${b.name}`;
        }
    });

    // 4. RESIDENTIAL POPULATION (Families)
    const occupiedHomes = new Set(npcs.filter(n => n.location.homeCoords).map(n => `${n.location.homeCoords.x},${n.location.homeCoords.y}`));

    residentialSites.forEach(home => {
        if (occupiedHomes.has(`${home.x},${home.y}`)) return; // Skip if already populated (e.g. leader)

        // Manor residents: lesser nobility / gentry families. A keep is never a commoner home:
        // if one is somehow still empty here it gets a noble household too.
        if (home.type === 'manor' || home.type === 'keep') {
            // The family is the one the manor is named for ("Ashwood Hall" -> Ashwood).
            const named = home.name && home.name.split(' ')[0];
            const familyName = named && NOBLE_LAST_NAMES.includes(named) ? named : rng.pick(NOBLE_LAST_NAMES);

            // Head of household — mix of landed knights and wealthy merchants
            const isKnight = rng.random() < 0.6;
            const head = addNPC(isKnight ? "Gentry" : "Merchant", home, home, { lastName: familyName });
            if (head.age < 25) head.age = rng.range(25, 45);
            const gentryOccupations = [
                "Retired Knight", "Landowner", "Magistrate", "Tax Collector",
                "Master of Coin", "Horse Breeder", "Patron of the Arts"
            ];
            const merchantOccupations = [
                "Wealthy Trader", "Trade Baron", "Import Merchant",
                "Spice Merchant", "Silk Trader", "Wine Merchant"
            ];
            head.job = `${head.title} ${head.name.split(' ')[1]} — ${rng.pick(isKnight ? gentryOccupations : merchantOccupations)}`;

            // Spouse
            const spouseGender = head.gender === "Male" ? "Female" : "Male";
            const spouse = addNPC("Gentry", home, home, {
                lastName: familyName,
                gender: spouseGender,
                titleIndex: head.selectedTitleIndex
            });
            spouse.age = head.age + rng.range(-4, 2);
            if (spouse.age < 20) spouse.age = 20;
            spouse.job = `${spouse.title} of the House ${familyName}`;

            // Children (1-3) with coherent ages
            const gentryYoungestParent = Math.min(head.age, spouse.age);
            const gentryOldestChildAge = Math.min(15, Math.max(1, gentryYoungestParent - 16));
            const childCount = rng.range(1, 3);
            let gentryChildAge = gentryOldestChildAge;
            for (let i = 0; i < childCount; i++) {
                const child = addNPC("Gentry Child", home, home, { lastName: familyName });
                child.age = gentryChildAge;
                gentryChildAge = Math.max(1, gentryChildAge - rng.range(1, 3));
                child.job = `${child.title} of the House ${familyName}`;
            }

            return;
        }

        const familyName = rng.pick(COMMON_LAST_NAMES);
        const childCount = rng.range(1, 4);

        // Generate house name based on family and town size
        const houseStyles = {
            hamlet: ["Cottage", "Homestead", "Hut"],
            village: ["Cottage", "Homestead", "House"],
            town: ["House", "Residence", "Dwelling"],
            city: ["Residence", "Townhouse", "House"]
        };
        const style = rng.pick(houseStyles[townSize] || houseStyles.village);
        mapData[home.y][home.x].buildingName = `The ${familyName} ${style}`;

        const canBeFarmer = workSites.length > 0 && (townSize === 'hamlet' || townSize === 'village' || townSize === 'town');

        // --- Generate head of household ---
        const headRole = canBeFarmer ? "Farmer" : "Villager";
        const head = addNPC(headRole, home, home, {
            lastName: familyName,
            title: headRole === "Farmer" ? "Farmer" : "Citizen"
        });
        head.familyRole = 'head';
        // Ensure head is old enough to have children (medieval: married young, but at least 25)
        if (head.age < 25) head.age = rng.range(25, 40);

        // --- Generate spouse ---
        const spouseGender = head.gender === "Male" ? "Female" : "Male";
        const spouseRole = canBeFarmer ? "Farmer" : "Villager";
        const spouse = addNPC(spouseRole, home, home, {
            lastName: familyName,
            gender: spouseGender,
            title: spouseRole === "Farmer" ? "Farmer" : "Citizen"
        });
        spouse.familyRole = 'spouse';
        // Spouse within a few years of head (medieval norm: wives often a bit younger)
        spouse.age = head.age + rng.range(-4, 2);
        if (spouse.age < 20) spouse.age = 20;

        // --- Assign parent jobs ---
        const assignParentJob = (npc) => {
            if (npc.role === "Farmer" && workSites.length > 0) {
                const site = workSites[rng.range(0, workSites.length - 1)];
                npc.location.x = site.x;
                npc.location.y = site.y;
                npc.location.buildingType = site.type;
                npc.job = site.type === 'field' ? "Tilling the fields" : "Tending to the barn";
            } else {
                // Balanced Vocation System
                if (!vocationSlots) {
                    const slotsBySize = {
                        hamlet: { "Cloth Weaver": 1, "Tool Mender": 1 },
                        village: { "Cloth Weaver": 1, "Tool Mender": 1, "Tanner": 1, "Tailor": 1, "Carpenter": 1 },
                        town: { "Cloth Weaver": 2, "Tool Mender": 2, "Tanner": 2, "Tailor": 2, "Carpenter": 2, "Ale Brewer": 2, "Baker": 2 },
                        city: { "Cloth Weaver": 5, "Tool Mender": 4, "Tanner": 4, "Tailor": 5, "Carpenter": 4, "Ale Brewer": 3, "Baker": 4 }
                    };
                    vocationSlots = { ...(slotsBySize[townSize] || slotsBySize.village) };
                }
                const availableVocations = Object.keys(vocationSlots).filter(v => vocationSlots[v] > 0);
                if (availableVocations.length > 0) {
                    const vocation = rng.pick(availableVocations);
                    npc.job = vocation;
                    vocationSlots[vocation]--;
                } else {
                    const domesticActivities = [
                        "Tending the hearth", "Cleaning the house", "Resting in the square",
                        "Trading at the market", "Mending nets", "Preparing a meal",
                        "Helping neighbors", "Running errands", "Fetching water"
                    ];
                    npc.job = rng.pick(domesticActivities);
                }
            }
        };
        assignParentJob(head);
        assignParentJob(spouse);

        // --- Generate children with coherent ages ---
        const youngestParent = Math.min(head.age, spouse.age);
        // Oldest child can be at most youngestParent - 16 (medieval: childbearing from ~16)
        const oldestChildMaxAge = Math.max(1, youngestParent - 16);
        // Cap at 15 (still a "child" in the household)
        const oldestChildAge = Math.min(15, oldestChildMaxAge);

        const childNpcs = [];
        for (let i = 0; i < childCount; i++) {
            const child = addNPC("Villager", home, home, {
                lastName: familyName,
                title: "Child"
            });
            child.familyRole = 'child';
            childNpcs.push(child);
        }

        // Assign staggered ages: oldest first, each subsequent 1-3 years younger
        let currentAge = oldestChildAge;
        // Sort children by their generated seed to maintain determinism, then overwrite ages
        childNpcs.forEach((child, i) => {
            if (i === 0) {
                child.age = currentAge;
            } else {
                currentAge = Math.max(1, currentAge - rng.range(1, 3));
                child.age = currentAge;
            }
            // Age-appropriate activities
            const childActivities = child.age >= 10
                ? ["Helping parents", "Running errands", "Exploring nearby", "Playing with friends", "Doing chores", "Learning a trade"]
                : ["Playing in the yard", "Helping parents", "Playing tag", "Chasing the cat", "Playing with a stick sword", "Picking wildflowers"];
            child.job = rng.pick(childActivities);
        });
    });

    return npcs;
};

/**
 * Add ONE milestone-authored NPC to an ALREADY-POPULATED town map (in-save
 * continuation retro-injection). Standalone sibling of populateTown's internal
 * addAuthoredNPC: same authored-identity forcing, but it targets an existing
 * roster instead of building one.
 *
 * Building match is by authored name first (injectQuestBuildings renames the quest
 * building, so the name is the reliable key), then by type. If that building
 * already houses an NPC from a PRIOR campaign (any npc carrying a milestoneNpcId),
 * the new authored NPC REPLACES it at the same post (same person, next chapter;
 * prevents duplicate quest-givers in shared venues); otherwise it is added
 * alongside the procedural staff.
 *
 * Mutates townMapData.npcs (callers pass a clone; see retroInjectQuestContent).
 *
 * @param {Object} townMapData - cached town map (with .mapData and .npcs)
 * @param {Object} spec - authored NPC spec from getMilestoneNpcsForTown
 * @param {number} seed - deterministic generation seed
 * @returns {Object|null} the placed NPC, or null if no matching building exists
 */
export const addAuthoredNpcToTown = (townMapData, spec, seed) => {
    if (!townMapData?.mapData || !spec) return null;

    // Locate the target building tile: authored name first, then type.
    let target = null;
    const wantName = spec.building?.name ? spec.building.name.toLowerCase() : null;
    const wantType = spec.building?.type || null;
    for (const match of ['name', 'type']) {
        for (let y = 0; y < townMapData.mapData.length && !target; y++) {
            for (let x = 0; x < townMapData.mapData[y].length && !target; x++) {
                const tile = townMapData.mapData[y][x];
                if (tile.type !== 'building') continue;
                if (match === 'name' && wantName && (tile.buildingName || '').toLowerCase() === wantName) {
                    target = { x, y, tile };
                } else if (match === 'type' && wantType && tile.buildingType === wantType) {
                    target = { x, y, tile };
                }
            }
        }
        if (target) break;
    }
    if (!target) return null;

    const role = ROLES[spec.role] ? spec.role : 'Guard';
    const npc = generateNPC({ seed, role, noEvil: true });
    npc.name = spec.name;
    if (spec.gender) npc.gender = spec.gender;
    if (spec.name.includes(' ')) npc.title = spec.name.split(' ')[0];
    if (spec.personality) npc.personality = spec.personality;
    npc.milestoneNpcId = spec.id;
    npc.milestoneId = spec.milestoneId;
    const buildingLabel = target.tile.buildingName || target.tile.buildingType;
    npc.job = `${authoredJobTitle(spec, npc)} at ${buildingLabel}`;
    npc.location = {
        x: target.x,
        y: target.y,
        buildingName: buildingLabel,
        buildingType: target.tile.buildingType,
        homeCoords: { x: target.x, y: target.y }
    };

    const roster = townMapData.npcs || [];
    // Prior campaign's authored NPC at this same building? Replace in place.
    const priorIdx = roster.findIndex((n) =>
        n.milestoneNpcId && n.milestoneNpcId !== spec.id &&
        n.location && n.location.x === target.x && n.location.y === target.y
    );
    if (priorIdx >= 0) {
        roster[priorIdx] = npc;
    } else {
        roster.push(npc);
    }
    townMapData.npcs = roster;
    return npc;
};

export default generateNPC;
